import {isEqual, isUndefined} from 'lodash';
import React, {ReactNode} from 'react';

import {Trans} from '../../../libs/shared/helpers/src/internationalization/react';
import {AppSelector, createSelector} from '../../../srcCommon/helpers/reselectHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {AnalyticsTableMetricsEnum, isResourceDataPoint} from '../../models/api/analyticsMetricsModel';
import {AnalyticsFilterTypesEnum} from '../../models/api/analyticsSavedViewModel';
import {getAnalyticsFilters} from '../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {AnalyticsPanelFiltersState} from '../../store/panels/analytics/filters/analyticsPanelFiltersState';
import {TableData} from '../components/reportElements/table/tableHelpers';

function isIdListFilter(
  filterType: AnalyticsFilterTypesEnum,
): filterType is
  | AnalyticsFilterTypesEnum.TAGS
  | AnalyticsFilterTypesEnum.EXCLUDE_TAGS
  | AnalyticsFilterTypesEnum.INBOXES
  | AnalyticsFilterTypesEnum.CHANNELS
  | AnalyticsFilterTypesEnum.TEAMMATES {
  return [
    AnalyticsFilterTypesEnum.TAGS,
    AnalyticsFilterTypesEnum.EXCLUDE_TAGS,
    AnalyticsFilterTypesEnum.INBOXES,
    AnalyticsFilterTypesEnum.CHANNELS,
    AnalyticsFilterTypesEnum.TEAMMATES,
  ].includes(filterType);
}

/**
 * Get the list of selected ids for a specific filter, or undefined if no filter is present
 * @param filters
 * @param filterType
 */
function getFilterIds(
  filters: AnalyticsPanelFiltersState,
  filterType: AnalyticsFilterTypesEnum,
): ReadonlySet<number> | undefined {
  if (!isIdListFilter(filterType)) {
    return undefined;
  }

  const filter = filters[filterType];

  if (Array.isArray(filter)) {
    return new Set(filter);
  }

  return undefined;
}

function makeFilterSelector(filterType: AnalyticsFilterTypesEnum, excludedFilter?: AnalyticsFilterTypesEnum) {
  return createSelector([getAnalyticsFilters], (filters) => {
    const includedIds = getFilterIds(filters, filterType);
    const excludedIds = (excludedFilter && getFilterIds(filters, excludedFilter)) ?? new Set();

    if (includedIds === undefined || includedIds.size === 0) {
      return {includedIds: new Set<number>(), excludedIds: new Set<number>()};
    }

    return {includedIds, excludedIds};
  });
}

function makeShownFilterSelector(filterType: AnalyticsFilterTypesEnum) {
  return createSelector([getAnalyticsFilters], (filters) => {
    const filter = getFilterIds(filters, filterType);
    return Boolean(filter && filter.size > 0);
  });
}

type AnalyticsTableFilters = {
  [key in AnalyticsTableMetricsEnum]?: {
    field: string;
    shownSelector: AppSelector<boolean>;
    selector: AppSelector<{includedIds: ReadonlySet<number>; excludedIds: ReadonlySet<number>}>;
    label: ReactNode;
  };
};

const analyticsTableFilters: AnalyticsTableFilters = {
  [AnalyticsTableMetricsEnum.TAGS_ALL_TABLE]: {
    shownSelector: makeShownFilterSelector(AnalyticsFilterTypesEnum.TAGS),
    selector: makeFilterSelector(AnalyticsFilterTypesEnum.TAGS, AnalyticsFilterTypesEnum.EXCLUDE_TAGS),
    field: 'tag_id',
    label: <Trans id="analytics-table-display-only-selected-tags">Display only selected tags</Trans>,
  },
  [AnalyticsTableMetricsEnum.TEAM_PERFORMANCE]: {
    shownSelector: makeShownFilterSelector(AnalyticsFilterTypesEnum.TEAMMATES),
    selector: makeFilterSelector(AnalyticsFilterTypesEnum.TEAMMATES),
    field: 'teammate_id',
    label: (
      <Trans id="analytics-table-display-only-selected-teammates">Display only selected teammates</Trans>
    ),
  },
  [AnalyticsTableMetricsEnum.TAG_CHANGES_TABLE]: {
    shownSelector: makeShownFilterSelector(AnalyticsFilterTypesEnum.TAGS),
    selector: makeFilterSelector(AnalyticsFilterTypesEnum.TAGS, AnalyticsFilterTypesEnum.EXCLUDE_TAGS),
    field: 'tag_id',
    label: <Trans id="analytics-table-display-only-selected-tags">Display only selected tags</Trans>,
  },
};

export function getTableIdFilterText(metric: AnalyticsTableMetricsEnum | undefined) {
  return metric !== undefined && analyticsTableFilters[metric]?.label;
}

export const useTableIdFilter = (
  metric: AnalyticsTableMetricsEnum | undefined,
  enabled: boolean | undefined,
) => {
  const filter = metric && analyticsTableFilters[metric];

  // We cannot return this early so using default values if we have incorrect data, and we'll return after the useSelector.
  const {
    field = '',
    selector = () => ({includedIds: new Set<number>(), excludedIds: new Set<number>()}),
    shownSelector = () => false,
  } = filter || {};

  const {includedIds, excludedIds} = useAppSelector(selector, isEqual);
  const showRowFilterCheckbox = useAppSelector(shownSelector);

  const isRowFilteredPredicate = (row: TableData) => {
    const dataPoint = row[field];
    if (!dataPoint || !isResourceDataPoint(dataPoint) || isUndefined(dataPoint.id)) {
      return true;
    }

    if (!includedIds.size) {
      return true;
    }

    return includedIds.has(dataPoint.id) && !excludedIds.has(dataPoint.id);
  };

  // If there is no filter, or the filtering is disabled, return a predicate that always returns true
  if (!enabled || !filter) {
    return {isRowFilteredPredicate: () => true, showRowFilterCheckbox};
  }

  return {isRowFilteredPredicate, showRowFilterCheckbox};
};
